import type { IDialogProps, IOmit } from '@sixui/ui';
import { useCallback, useEffect, useState } from 'react';
import { faFolderImage } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createSequence } from '@olivierpascal/helpers';
import { Button, Dialog, InputChip, Skeleton, Stack } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type {
  IFileUploaderFileType,
  IFileUploaderValue,
} from '~/components/organisms/fields/FileUploader';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Pagination } from '~/components/molecules/Pagination';
import { IUsePaginationResult } from '~/hooks/usePagination';
import { useDocumentsQuery } from '~/lib/api/modules/Documents/hooks/useDocumentsQuery';
import { usePicturesQuery } from '~/lib/api/modules/Pictures/hooks/usePicturesQuery';
import { useVideosQuery } from '~/lib/api/modules/Videos/hooks/useVideosQuery';
import { areFileValuesEqual } from '../fields/FileUploader/areFileValuesEqual';
import { FileGalleryDialogFigure } from './FileGalleryDialogFigure';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 12;

export type IFileGalleryDialogProps = IOmit<
  IDialogProps,
  'headline' | 'actions' | 'children'
> & {
  appId: string;
  acceptTypes?: Array<IFileUploaderFileType>;
  multi?: boolean;
  onSelect: (selectedFiles: Array<IFileUploaderValue>) => void;
};

const styles = stylex.create({
  host: {
    width: 680,
  },
  gallery: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridTemplateRows: 'repeat(3, 120px)',
    gap: 8,
  },
});

export const FileGalleryDialog: React.FC<IFileGalleryDialogProps> = (props) => {
  const {
    appId,
    onSelect,
    acceptTypes = ['picture', 'video', 'document'],
    multi,
    ...other
  } = props;
  const { t } = useTranslation();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [activeType, setActiveType] = useState(acceptTypes[0]);
  const [selectedFiles, setSelectedFiles] = useState<Array<IFileUploaderValue>>(
    [],
  );
  const pagination: IUsePaginationResult = {
    start: (page - 1) * limit,
    limit,
    page,
    updateLimit: setLimit,
    updatePage: setPage,
  };
  const picturesQuery = usePicturesQuery(
    other.opened && activeType === 'picture'
      ? {
          appId,
          body: {
            start: pagination.start,
            limit: pagination.limit,
          },
        }
      : undefined,
    {
      enabled: other.opened && activeType === 'picture',
      keepPreviousData: true,
    },
  );
  const videosQuery = useVideosQuery(
    other.opened && activeType === 'video'
      ? {
          appId,
          body: {
            start: pagination.start,
            limit: pagination.limit,
          },
        }
      : undefined,
    {
      enabled: other.opened && activeType === 'video',
      keepPreviousData: true,
    },
  );
  const documentsQuery = useDocumentsQuery(
    other.opened && activeType === 'document'
      ? {
          appId,
          body: {
            start: pagination.start,
            limit: pagination.limit,
          },
        }
      : undefined,
    {
      enabled: other.opened && activeType === 'document',
      keepPreviousData: true,
    },
  );

  const activeQuery =
    activeType === 'picture'
      ? picturesQuery
      : activeType === 'video'
        ? videosQuery
        : documentsQuery;
  const isEmpty = !activeQuery.isLoading && !activeQuery.data?.count;

  useEffect(() => {
    // Reset selected files when dialog is closed.
    if (props.opened) {
      return;
    }

    setSelectedFiles([]);
  }, [props.opened]);

  const handleSelectFile = useCallback(
    (file: IFileUploaderValue): void => {
      if (multi) {
        setSelectedFiles((prevSelectedFiles) => {
          const isSelected = prevSelectedFiles.some((selectedFile) =>
            areFileValuesEqual(selectedFile, file),
          );

          return isSelected
            ? prevSelectedFiles.filter(
                (selectedFile) => !areFileValuesEqual(selectedFile, file),
              )
            : [...prevSelectedFiles, file];
        });
      } else {
        onSelect([file]);
      }
    },
    [multi, onSelect],
  );

  return (
    <Dialog
      headline={t('FileGalleryDialog.title')}
      icon={<FontAwesomeIcon icon={faFolderImage} />}
      sx={styles.host}
      actions={({ close }) => (
        <>
          <Button variant="text" onClick={close}>
            {t('common.cancel')}
          </Button>
          {multi && (
            <Button onClick={() => onSelect(selectedFiles)}>
              {t('common.insert')}
            </Button>
          )}
        </>
      )}
      {...other}
    >
      <Stack gap={4}>
        <Stack horizontal gap={3} justify="center">
          {acceptTypes?.map((acceptType) => (
            <InputChip
              key={acceptType}
              label={t(`FileGalleryDialog.tabs.${acceptType}.label`)}
              selected={activeType === acceptType}
              onClick={() => setActiveType(acceptType)}
            />
          ))}
        </Stack>

        <div {...stylex.props(styles.gallery)}>
          {activeQuery.isError ? (
            <ErrorMessage error={activeQuery.error} />
          ) : isEmpty ? (
            t('FileGalleryDialog.empty')
          ) : activeQuery.isLoading || activeQuery.isPreviousData ? (
            createSequence(DEFAULT_LIMIT).map((index) => (
              <Skeleton key={index}>
                <FileGalleryDialogFigure />
              </Skeleton>
            ))
          ) : (
            activeQuery.data?.list?.map((media, mediaIndex) => (
              <FileGalleryDialogFigure
                key={mediaIndex}
                src={media.thumbUrl}
                title={media.name}
                onClick={() =>
                  handleSelectFile({
                    type: activeType,
                    id: media._id,
                  })
                }
                selected={selectedFiles.some(
                  (selectedFile) =>
                    selectedFile.type === activeType &&
                    selectedFile.id === media._id,
                )}
              />
            ))
          )}
        </div>
        <Pagination
          pagination={pagination}
          total={activeQuery.data?.count}
          rowsPerPageOptions={[pagination.limit]}
        />
      </Stack>
    </Dialog>
  );
};
